import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { FileText, PenTool, User, LogOut, Loader2, Save, Upload, MapPin, Mail, Phone, ShoppingCart, Download, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Footer from '../components/sections/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserProfile = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const email = localStorage.getItem('admin_email') || '';
  const token = localStorage.getItem('admin_token');
  const role = localStorage.getItem('admin_role');
  const [tab, setTab] = useState('quotes');
  const [quotes, setQuotes] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [orders, setOrders] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [profile, setProfile] = useState({ name: '', phone: '', email: '', address: '', bio: '', avatar: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (role === 'admin' || role === 'sales') { navigate('/admin-dashboard'); return; }
    if (!token || !email) { navigate('/profile'); return; }
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      // First load profile to get both email and phone
      const pRes = await axios.get(`${API}/admin/user-profile/${encodeURIComponent(email)}`);
      if (pRes.data?.account) setProfile(pRes.data);
      const profEmail = pRes.data?.email || email;
      const profPhone = pRes.data?.phone || '';
      const ep = `email=${encodeURIComponent(profEmail)}&phone=${encodeURIComponent(profPhone)}`;
      const [qRes, dRes, oRes, aRes, rRes] = await Promise.all([
        axios.get(`${API}/admin/quotes/by-email?${ep}`),
        axios.get(`${API}/admin/design-logs/by-account?account=${encodeURIComponent(profEmail)}&phone=${encodeURIComponent(profPhone)}`),
        axios.get(`${API}/shop/orders/by-account?account=${encodeURIComponent(profEmail)}&phone=${encodeURIComponent(profPhone)}`),
        axios.get(`${API}/shop/assignments/by-account?account=${encodeURIComponent(email)}`),
        axios.get(`${API}/admin/registrations/by-email?${ep}`),
      ]);
      setQuotes(qRes.data.quotes || []);
      setDesigns(dRes.data.logs || []);
      setOrders(oRes.data.orders || []);
      setAssignments(aRes.data.assignments || []);
      setRegistrations(rRes.data.registrations || []);
    } catch {}
    finally { setLoading(false); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/user-profile/${encodeURIComponent(email)}`, profile);
      toast.success(isZh ? '保存成功' : 'Saved');
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const uploadAvatar = () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0]; if (!file) return;
      try {
        const form = new FormData(); form.append('file', file);
        const h = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await axios.post(`${API}/admin/upload`, form, { headers: h });
        setProfile(p => ({...p, avatar: r.data.url}));
        toast.success(isZh ? '头像已上传' : 'Avatar uploaded');
      } catch { toast.error('Upload failed'); }
    };
    input.click();
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_role');
    localStorage.removeItem('admin_email');
    navigate('/');
  };

  if (!token) return null;

  const tabs = [
    { id: 'quotes', icon: FileText, label: isZh ? '我的报价' : 'My Quotes' },
    { id: 'designs', icon: PenTool, label: isZh ? '我的设计' : 'My Design' },
    { id: 'orders', icon: ShoppingCart, label: isZh ? '我的订单' : 'My Orders' },
    { id: 'registrations', icon: Calendar, label: isZh ? '我的报名' : 'My Events' },
    { id: 'info', icon: User, label: isZh ? '我的信息' : 'My Information' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pt-20">
      <main className="flex-grow">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-red-100 overflow-hidden flex items-center justify-center">
                {profile.avatar ? <img src={profile.avatar} alt="" className="w-full h-full object-cover" /> : <User className="w-7 h-7 text-red-600" />}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{isZh ? '个人中心' : 'My Account'}</h1>
                <p className="text-gray-500 text-sm">{email}</p>
              </div>
            </div>
            <Button onClick={logout} variant="outline" className="border-gray-300"><LogOut className="w-4 h-4 mr-2" />{isZh ? '退出' : 'Logout'}</Button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === t.id ? 'bg-red-600 text-white shadow' : 'bg-white text-gray-600 border hover:bg-gray-50'}`}>
                <t.icon className="w-4 h-4" />{t.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-red-600 mx-auto" /></div>
          ) : (
            <>
              {/* My Quotes */}
              {tab === 'quotes' && (
                <div className="space-y-4">
                  {quotes.length === 0 ? (
                    <Card><CardContent className="p-12 text-center text-gray-400">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>{isZh ? '暂无报价记录' : 'No quotes found'}</p>
                      <Button onClick={() => navigate('/quote')} className="mt-4 bg-red-600 text-white">{isZh ? '去生成报价' : 'Get a Quote'}</Button>
                    </CardContent></Card>
                  ) : (
                    quotes.map((q, i) => (
                      <Card key={i} className="shadow-sm hover:shadow-md transition-shadow">
                        <CardContent className="p-5">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-bold text-gray-900">{q.property_address}</h3>
                              <p className="text-sm text-gray-500 mt-1">{q.package_type?.toUpperCase()} · {q.bedrooms}B/{q.bathrooms}BA · {q.square_footage}sf</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-black text-red-600">${q.total_price?.toLocaleString()}</p>
                              <p className="text-xs text-gray-400">{q.created_at?.split('T')[0]}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              )}

              {/* My Designs */}
              {tab === 'designs' && (
                <div className="space-y-4">
                  {designs.length === 0 ? (
                    <Card><CardContent className="p-12 text-center text-gray-400">
                      <PenTool className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>{isZh ? '暂无设计记录' : 'No design records'}</p>
                      <Button onClick={() => navigate('/design')} className="mt-4 bg-red-600 text-white">{isZh ? '开始设计' : 'Start Design'}</Button>
                    </CardContent></Card>
                  ) : (
                    designs.map((d, i) => (
                      <Card key={i} className="shadow-sm">
                        <CardContent className="p-5">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-bold text-gray-900">{d.address || 'Design'}</h3>
                              <p className="text-sm text-gray-500 mt-1">{d.building_type?.toUpperCase()} · {d.stories} · {d.bedrooms}B/{d.bathrooms}BA · {d.sqft}sf</p>
                              {d.preferences?.length > 0 && <p className="text-xs text-gray-400 mt-1">{d.preferences.join(', ')}</p>}
                            </div>
                            <p className="text-xs text-gray-400">{d.timestamp?.replace('T',' ').split('.')[0]}</p>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              )}

              {/* My Orders */}
              {tab === 'orders' && (
                <div className="space-y-4">
                  {/* Assigned packages — pending selection */}
                  {assignments.filter(a => a.status !== 'completed').length > 0 && (
                    <div className="mb-6">
                      <h2 className="text-sm font-bold text-amber-600 mb-3 flex items-center gap-1">⏳ {isZh ? '待处理 — 顾问分配的套餐' : 'Pending — Assigned Packages'}</h2>
                      {assignments.filter(a => a.status !== 'completed').map(a => (
                        <Card key={a.id} className="shadow-md border-amber-200 mb-3">
                          <CardContent className="p-5 flex items-center justify-between">
                            <div>
                              <h3 className="font-bold text-gray-900">{isZh ? '专属选材方案' : 'Material Selection Assignment'}</h3>
                              <p className="text-sm text-gray-500">{isZh ? '顾问' : 'Consultant'}: {a.consultant} · {a.sign_date}</p>
                              <p className="text-sm text-gray-500">{a.sign_address}</p>
                            </div>
                            <Button onClick={() => navigate(`/shop/assign/${a.id}`)} className="bg-amber-500 hover:bg-amber-600 text-white font-bold">
                              {isZh ? '去选择' : 'Select Now'} →
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}

                  {/* Orders */}
                  {orders.length === 0 && assignments.length === 0 ? (
                    <Card><CardContent className="p-12 text-center text-gray-400">
                      <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>{isZh ? '暂无订单' : 'No orders yet'}</p>
                      <Button onClick={() => navigate('/shop')} className="mt-4 bg-red-600 text-white">{isZh ? '去选购' : 'Start Shopping'}</Button>
                    </CardContent></Card>
                  ) : (<div>
                    <div className="flex gap-2 mb-3">
                      <button onClick={async () => { const { exportOrdersExcel } = await import('../utils/orderExport'); exportOrdersExcel(orders, isZh); }} className="text-xs px-3 py-1.5 border rounded hover:border-red-600 hover:text-red-600 flex items-center gap-1"><Download className="w-3 h-3" />Excel</button>
                    </div>
                    {orders.map((o) => (
                      <Card key={o.id} className="shadow-sm">
                        <CardContent className="p-5">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-2">
                              <div>
                                <p className="font-mono text-sm font-bold text-gray-900">{o.order_no}</p>
                                <p className="text-xs text-gray-400">{o.created_at?.split('T')[0]}</p>
                              </div>
                              <button onClick={async () => { const { exportOrderPDF } = await import('../utils/orderExport'); exportOrderPDF(o, isZh); }} className="text-xs px-2 py-1 border rounded text-gray-400 hover:text-red-600 hover:border-red-600" title="Export PDF"><Download className="w-3 h-3" /></button>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-black text-red-600">${o.total_price?.toFixed(2)}</p>
                              <span className={`text-xs px-2 py-0.5 rounded ${o.status === 'completed' ? 'bg-green-100 text-green-700' : o.status === 'cancelled' ? 'bg-red-100 text-red-700' : o.status === 'signed' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{o.status}</span>
                              {o.status !== 'pending' && <p className="text-xs text-gray-400 mt-1">{isZh ? '仅查看' : 'View only'}</p>}
                            </div>
                          </div>
                          <div className="space-y-1">
                            {(o.items || []).map((item, i) => (
                              <div key={i} className="flex items-center gap-3 text-gray-600 py-2">
                                {item.spec_image && <img src={item.spec_image} alt="" className="w-48 h-48 rounded-lg object-contain bg-gray-50 hover-zoom-img" />}
                                <div className="flex-1">
                                  <span className="font-bold text-sm text-gray-900">{isZh ? item.name_zh : item.name_en}</span>
                                  {item.spec_name && <span className="text-sm text-gray-500 ml-2">{item.spec_name}</span>}
                                </div>
                                <span className="font-bold text-sm">${item.price} x{item.quantity || 1}</span>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  )}
                </div>
              )}

              {/* My Events / Registrations */}
              {tab === 'registrations' && (
                <div>
                  {registrations.length === 0 ? (
                    <Card className="shadow"><CardContent className="p-12 text-center text-gray-400">
                      <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>{isZh ? '暂无报名记录' : 'No event registrations'}</p>
                    </CardContent></Card>
                  ) : (
                    <div className="space-y-3">
                      {registrations.map(r => (
                        <Card key={r.id} className="shadow-sm hover:shadow-md transition-shadow">
                          <CardContent className="p-5 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                <Calendar className="w-5 h-5 text-green-600" />
                              </div>
                              <div>
                                <p className="font-bold text-gray-900 text-sm">{isZh ? (r.event_title_zh || r.event_title_en) : (r.event_title_en || r.event_title_zh)}</p>
                                <p className="text-xs text-gray-500">{r.first_name} {r.last_name} · {r.phone}</p>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <span className="inline-block bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full">{isZh ? '报名成功' : 'Registered'}</span>
                              <p className="text-xs text-gray-400 mt-1">{r.created_at?.split('T')[0]}</p>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* My Information */}
              {tab === 'info' && (
                <Card className="shadow-lg">
                  <CardContent className="p-8 space-y-5">
                    {/* Avatar */}
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <div className="w-20 h-20 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center">
                          {profile.avatar ? <img src={profile.avatar} alt="" className="w-full h-full object-cover" /> : <User className="w-10 h-10 text-gray-300" />}
                        </div>
                        <button type="button" onClick={uploadAvatar} className="absolute -bottom-1 -right-1 w-7 h-7 bg-red-600 text-white rounded-full flex items-center justify-center shadow"><Upload className="w-3.5 h-3.5" /></button>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">{email}</p>
                        <p className="text-sm text-gray-400">{isZh ? '点击图标更换头像' : 'Click icon to change avatar'}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-bold text-gray-600 flex items-center gap-1"><User className="w-3.5 h-3.5" />{isZh ? '姓名' : 'Name'}</label>
                        <input value={profile.name || ''} onChange={e => setProfile(p => ({...p, name: e.target.value}))} className="w-full px-4 py-2.5 border rounded-lg mt-1 focus:border-red-600 outline-none" />
                      </div>
                      <div>
                        <label className="text-sm font-bold text-gray-600 flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{isZh ? '电话' : 'Phone'}</label>
                        <input value={profile.phone || ''} onChange={e => setProfile(p => ({...p, phone: e.target.value}))} className="w-full px-4 py-2.5 border rounded-lg mt-1 focus:border-red-600 outline-none" />
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-bold text-gray-600 flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{isZh ? '邮箱' : 'Email'}</label>
                      <input value={profile.email || ''} onChange={e => setProfile(p => ({...p, email: e.target.value}))} className="w-full px-4 py-2.5 border rounded-lg mt-1 focus:border-red-600 outline-none" />
                    </div>

                    <div>
                      <label className="text-sm font-bold text-gray-600 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{isZh ? '地址' : 'Address'}</label>
                      <input value={profile.address || ''} onChange={e => setProfile(p => ({...p, address: e.target.value}))} className="w-full px-4 py-2.5 border rounded-lg mt-1 focus:border-red-600 outline-none" />
                    </div>

                    <div>
                      <label className="text-sm font-bold text-gray-600">{isZh ? '简介' : 'Bio'}</label>
                      <textarea value={profile.bio || ''} onChange={e => setProfile(p => ({...p, bio: e.target.value}))} rows={3} className="w-full px-4 py-2.5 border rounded-lg mt-1 focus:border-red-600 outline-none" />
                    </div>

                    <Button onClick={saveProfile} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-3">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}{isZh ? '保存' : 'Save'}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default UserProfile;
